import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Mail, User, LogOut, Settings, FileText, Bell, Calendar, CheckCheck } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notificationCount, setNotificationCount] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isAuthenticated } = useAuth();
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  
  const fetchNotifications = useCallback(async () => {
    if (!isAuthenticated || !user) return;
    try {
      const response = await axios.get(`${backendUrl}/api/notifications`);
      setNotifications(response.data.notifications || []);
      setNotificationCount(response.data.unread_count || 0);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  }, [isAuthenticated, user, backendUrl]);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const handleMarkAllRead = async () => {
    try {
      await axios.put(`${backendUrl}/api/notifications/read-all`);
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setNotificationCount(0);
    } catch (error) {
      console.error('Error marking all read:', error);
    }
  };

  const isActive = (path) => location.pathname === path;

  const getInitials = (name) => {
    const parts = (name || '').split(' ').filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return (name || 'U').substring(0, 2).toUpperCase();
  };

  const handleLogout = () => {
    logout();
    setUserMenuOpen(false);
    setMobileMenuOpen(false);
    navigate('/');
  };

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'Membership', path: user?.membership_type === 'student' ? '/membership/students' : '/membership/doctors' },
    { name: 'Find a Practitioner', path: '/practitioners' },
    { name: 'Events', path: '/events' },
    { name: 'Education', path: '/education', memberOnly: true },
    { name: 'News', path: '/news' },
    { name: 'Contact', path: '/contact' }
  ].filter(l => {
    if (l.publicOnly && isAuthenticated) return false;
    if (l.memberOnly && !isAuthenticated) return false;
    return true;
  });

  return (
    <header className="sticky top-0 z-50">
      {/* Top Bar - Navy Blue */}
      <div className="bg-[#2c3e7d] text-white">
        <div className="container mx-auto px-4 py-2">
          <div className="flex items-center justify-end">
            <Mail size={16} className="mr-2" />
            <a href="mailto:admin@chirosport.co.za" className="text-sm hover:underline">
              admin@chirosport.co.za
            </a>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <div className="bg-white shadow-md">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center">
              <img
                src="/brand/chirosport-logo.png"
                alt="Chirosport SA"
                className="h-16 md:h-20"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'block';
                }}
              />
              <div style={{ display: 'none' }} className="flex items-center space-x-2">
                <div className="text-2xl font-bold">
                  <span className="text-blue-600">CHIRO</span>
                  <span className="text-red-600">SPORT</span>
                </div>
                <div className="text-xs text-gray-600">
                  <div>SOUTH</div>
                  <div>AFRICA</div>
                </div>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-2">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`px-5 py-2.5 rounded-full text-sm font-medium transition-colors ${
                    isActive(link.path)
                      ? 'bg-[#2c3e7d] text-white'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              
              {/* User Menu or Login/Register */}
              {isAuthenticated && user ? (
                <div className="flex items-center gap-3">
                  {/* Notification Bell */}
                  <div className="relative">
                    <button
                      onClick={() => setNotificationOpen(!notificationOpen)}
                      className="relative p-2 hover:bg-gray-100 rounded-full transition-colors"
                      data-testid="notification-bell"
                    >
                      <Bell size={20} className="text-gray-700" />
                      {notificationCount > 0 && (
                        <span className="absolute top-0 right-0 bg-red-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
                          {notificationCount}
                        </span>
                      )}
                    </button>
                    {notificationOpen && (
                      <div className="absolute right-0 mt-2 w-80 bg-white rounded-md shadow-lg z-50 py-1 border border-gray-200 max-h-96 overflow-y-auto">
                        <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 flex justify-between items-center">
                          <p className="text-xs uppercase text-gray-500 font-semibold">Notifications</p>
                          {notificationCount > 0 && (
                            <button
                              onClick={handleMarkAllRead}
                              className="text-xs text-[#2c3e7d] hover:underline flex items-center gap-1"
                            >
                              <CheckCheck size={14} />
                              Mark all read
                            </button>
                          )}
                        </div>
                        {notifications.length === 0 ? (
                          <div className="px-4 py-6 text-center text-gray-500 text-sm">
                            No notifications
                          </div>
                        ) : (
                          notifications.slice(0, 10).map(n => (
                            <div
                              key={n.id}
                              className={`px-4 py-3 border-b border-gray-100 hover:bg-gray-50 cursor-pointer ${!n.is_read ? 'bg-blue-50' : ''}`}
                              onClick={async () => {
                                if (!n.is_read) {
                                  try {
                                    await axios.put(`${backendUrl}/api/notifications/${n.id}/read`);
                                    setNotifications(prev => prev.map(item => item.id === n.id ? { ...item, is_read: true } : item));
                                    setNotificationCount(prev => Math.max(0, prev - 1));
                                  } catch (e) { console.error(e); }
                                }
                              }}
                            >
                              <p className="text-sm font-medium text-gray-900">{n.title}</p>
                              <p className="text-xs text-gray-600 mt-1">{n.message}</p>
                              <p className="text-xs text-gray-400 mt-1">
                                {n.created_at ? new Date(n.created_at).toLocaleString() : ''}
                              </p>
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </div>

                  {/* User Avatar Dropdown */}
                  <div className="relative">
                    <button
                      onClick={() => setUserMenuOpen(!userMenuOpen)}
                      className="flex items-center space-x-2 hover:bg-gray-100 rounded-full p-1 transition-colors"
                    >
                      <div className="w-10 h-10 rounded-full bg-[#2c3e7d] flex items-center justify-center text-white font-semibold">
                        {getInitials(user.full_name)}
                      </div>
                    </button>
                  
                  {userMenuOpen && (
                    <div className="absolute right-0 mt-2 w-64 bg-white rounded-md shadow-lg z-50 py-1 border border-gray-200">
                      <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
                        <p className="text-xs uppercase text-gray-500 font-semibold">WELCOME {user.full_name?.toUpperCase()}</p>
                      </div>
                      <Link
                        to="/profile"
                        onClick={() => setUserMenuOpen(false)}
                        className="flex items-center px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        <User size={16} className="mr-3" />
                        My Profile
                      </Link>
                      <Link
                        to="/change-password"
                        onClick={() => setUserMenuOpen(false)}
                        className="flex items-center px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        <Settings size={16} className="mr-3" />
                        Change Password
                      </Link>
                      <Link
                        to="/documents"
                        onClick={() => setUserMenuOpen(false)}
                        className="flex items-center px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        <FileText size={16} className="mr-3" />
                        Documents
                      </Link>
                      <Link
                        to="/my-events"
                        onClick={() => setUserMenuOpen(false)}
                        className="flex items-center px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        <Calendar size={16} className="mr-3" />
                        My Events
                      </Link>
                      {(user.role === 'super_admin' || user.role === 'admin' || user.role === 'executive') && (
                        <Link
                          to="/admin"
                          onClick={() => setUserMenuOpen(false)}
                          className="flex items-center px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-100"
                        >
                          <Settings size={16} className="mr-3" />
                          System Admin
                        </Link>
                      )}
                      {(user.role === 'super_admin' || user.role === 'executive' || user.role === 'admin') && (
                        <Link
                          to="/executive"
                          onClick={() => setUserMenuOpen(false)}
                          className="flex items-center px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-100"
                        >
                          <Settings size={16} className="mr-3" />
                          Executive Members
                        </Link>
                      )}
                      <button
                        onClick={handleLogout}
                        className="flex items-center w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        <LogOut size={16} className="mr-3" />
                        Sign out
                      </button>
                      <div className="px-4 py-3 bg-[#2c3e7d] text-white">
                        <p className="text-sm font-semibold">
                          MY POINTS : {user.points !== undefined ? user.points.toFixed(1) : '0.0'}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
                </div>
              ) : (
                <div className="relative group">
                  <button className="px-5 py-2.5 rounded-full text-sm font-medium text-gray-700 hover:bg-gray-100 flex items-center">
                    Membership
                    <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 border border-gray-200">
                    <Link
                      to="/login"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-t-md"
                    >
                      Login
                    </Link>
                    <Link
                      to="/register"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-b-md"
                    >
                      Register
                    </Link>
                  </div>
                </div>
              )}
            </nav>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden p-2 text-gray-700 hover:bg-gray-100 rounded-md"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <div className="lg:hidden mt-4 pb-4 border-t pt-4">
              <nav className="flex flex-col space-y-2">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`py-2 px-4 rounded-full text-sm font-medium transition-colors ${
                      isActive(link.path)
                        ? 'bg-[#2c3e7d] text-white'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    {link.name}
                  </Link>
                ))}
                <div className="pt-3 border-t space-y-2">
                  {isAuthenticated && user ? (
                    <>
                      <div className="px-4 py-2 bg-gray-50 rounded-md">
                        <p className="text-xs uppercase text-gray-500 font-semibold">WELCOME {user.full_name?.toUpperCase()}</p>
                      </div>
                      <Link
                        to="/profile"
                        onClick={() => setMobileMenuOpen(false)}
                        className="flex items-center py-2 px-4 text-sm text-gray-700 hover:bg-gray-100 rounded-md"
                      >
                        <User size={16} className="mr-2" />
                        My Profile
                      </Link>
                      <Link
                        to="/change-password"
                        onClick={() => setMobileMenuOpen(false)}
                        className="flex items-center py-2 px-4 text-sm text-gray-700 hover:bg-gray-100 rounded-md"
                      >
                        <Settings size={16} className="mr-2" />
                        Change Password
                      </Link>
                      <Link
                        to="/documents"
                        onClick={() => setMobileMenuOpen(false)}
                        className="flex items-center py-2 px-4 text-sm text-gray-700 hover:bg-gray-100 rounded-md"
                      >
                        <FileText size={16} className="mr-2" />
                        Documents
                      </Link>
                      {(user.role === 'super_admin' || user.role === 'admin' || user.role === 'executive') && (
                        <Link
                          to="/admin"
                          onClick={() => setMobileMenuOpen(false)}
                          className="flex items-center py-2 px-4 text-sm text-gray-700 hover:bg-gray-100 rounded-md"
                        >
                          <Settings size={16} className="mr-2" />
                          System Admin
                        </Link>
                      )}
                      {(user.role === 'super_admin' || user.role === 'executive' || user.role === 'admin') && (
                        <Link
                          to="/executive"
                          onClick={() => setMobileMenuOpen(false)}
                          className="flex items-center py-2 px-4 text-sm text-gray-700 hover:bg-gray-100 rounded-md"
                        >
                          <Settings size={16} className="mr-2" />
                          Executive Members
                        </Link>
                      )}
                      <button
                        onClick={handleLogout}
                        className="flex items-center w-full py-2 px-4 text-sm text-gray-700 hover:bg-gray-100 rounded-md"
                      >
                        <LogOut size={16} className="mr-2" />
                        Sign out
                      </button>
                      <div className="px-4 py-2 bg-[#2c3e7d] text-white rounded-md">
                        <p className="text-sm font-semibold">
                          MY POINTS : {user.points !== undefined ? user.points.toFixed(1) : '0.0'}
                        </p>
                      </div>
                    </>
                  ) : (
                    <>
                      <Link
                        to="/login"
                        onClick={() => setMobileMenuOpen(false)}
                        className="block py-2 px-4 text-sm text-gray-700 hover:bg-gray-100 rounded-md"
                      >
                        Login
                      </Link>
                      <Link
                        to="/register"
                        onClick={() => setMobileMenuOpen(false)}
                        className="block py-2 px-4 text-sm text-gray-700 hover:bg-gray-100 rounded-md"
                      >
                        Register
                      </Link>
                    </>
                  )}
                </div>
              </nav>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;